import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { resolveAssetUrl } from "../../utils/resolveAssetUrl";
import useCustomerAuth from "../../hooks/useCustomerAuth";

const defaultHeader = {
  brand: "Homevilas",
  badge: "Luxury stays",
  signInLabel: "Account",
  signInLink: "/customer/login",
  loginLinks: [
    { label: "Customer login", href: "/customer/login" },
    { label: "Customer signup", href: "/customer/signup" },
    { label: "Host login", href: "/host/login" },
    { label: "Host signup", href: "/host/signup" },
  ],
  ctaLabel: "Discover stays",
  ctaLink: "/listings",
  logoUrl: "/logo512.png?v=homevilas-new",
};

const ServiceIcon = ({ children, tone }) => (
  <span
    className={`flex h-11 w-11 items-center justify-center rounded-2xl shadow-soft-sm ring-1 ${
      tone || "bg-white text-[var(--ink)] ring-[var(--border)]"
    }`}
  >
    {children}
  </span>
);

const navItems = [
  {
    label: "Home",
    href: "/",
    tone: "bg-[rgba(15,92,90,0.08)] text-[var(--cta-dark)] ring-[rgba(15,92,90,0.12)]",
    icon: (
      <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.6">
        <path d="M3 10.5 12 4l9 6.5" />
        <path d="M5 9.8V20h14V9.8" />
        <path d="M9 20v-6h6v6" />
      </svg>
    ),
  },
  {
    label: "Villas & Homestays",
    href: "/listings",
    tone: "bg-[rgba(15,92,90,0.08)] text-[var(--cta-dark)] ring-[rgba(15,92,90,0.12)]",
    icon: (
      <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.6">
        <path d="M3 10.5 12 4l9 6.5" />
        <path d="M5 9.8V20h14V9.8" />
        <path d="M9 20v-6h6v6" />
      </svg>
    ),
  },
  {
    label: "Cabs",
    href: "/cabs",
    tone: "bg-[rgba(201,161,91,0.14)] text-[var(--gold)] ring-[rgba(201,161,91,0.18)]",
    icon: (
      <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.6">
        <path d="M4 12.5l1.8-4.3A2 2 0 0 1 7.7 7h8.6a2 2 0 0 1 1.9 1.2L20 12.5" />
        <path d="M3.5 12.5h17v4.5a1 1 0 0 1-1 1H4.5a1 1 0 0 1-1-1z" />
        <circle cx="7.5" cy="18" r="1.2" />
        <circle cx="16.5" cy="18" r="1.2" />
      </svg>
    ),
  },
  {
    label: "Holiday Packages",
    href: "/packages",
    tone: "bg-[rgba(15,92,90,0.08)] text-[var(--cta-dark)] ring-[rgba(15,92,90,0.12)]",
    icon: (
      <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.6">
        <path d="M5 18c3-7 11-9 14-9" />
        <path d="M12 6c3.8 0 7 1.7 7 3.8 0 1.5-1.6 2.8-3.9 3.4" />
        <path d="M12 6v12" />
        <path d="M8 20h8" />
      </svg>
    ),
  },
  {
    label: "Become a Host",
    href: "/become-a-host",
    tone: "bg-[rgba(15,92,90,0.08)] text-[var(--cta-dark)] ring-[rgba(15,92,90,0.12)]",
    icon: (
      <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.6">
        <path d="M12 4a4 4 0 1 1-4 4 4 4 0 0 1 4-4z" />
        <path d="M5 20a7 7 0 0 1 14 0" />
      </svg>
    ),
  },
];

const normalizeLink = (item, fallbackLabel) => {
  if (typeof item === "string") {
    return { label: item || fallbackLabel, href: "#" };
  }
  return {
    ...(item || {}),
    label: item?.label || fallbackLabel,
    href: item?.href || "#",
  };
};

const renderNavLink = (item, className, onClick) => {
  const href = String(item?.href || "#");
  const label = item?.label || "Link";
  if (href.startsWith("/")) {
    return (
      <Link to={href} className={className} onClick={onClick}>
        {label}
      </Link>
    );
  }
  return (
    <a href={href} className={className} onClick={onClick}>
      {label}
    </a>
  );
};

export default function LandingHeader() {
  const navigate = useNavigate();
  const location = useLocation();
  const customerAuth = useCustomerAuth();
  const [loginOpen, setLoginOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const loginMenuRef = useRef(null);

  const header = useMemo(() => {
    const merged = { ...defaultHeader };
    const loginLinks = (merged.loginLinks || []).map((item) =>
      normalizeLink(item, "Login")
    );
    return {
      ...merged,
      loginLinks: loginLinks.length
        ? loginLinks
        : [normalizeLink({ label: merged.signInLabel, href: merged.signInLink })],
    };
  }, []);

  const rawLogoUrl = header.logoUrl || header.logo_url || header.logo || "";
  const customer = customerAuth.customer;
  const accountLabel = customer
    ? String(customer.full_name || customer.name || customer.email || customer.phone || "Account").split(/\s+/)[0]
    : header.signInLabel;
  const logoUrl =
    rawLogoUrl.startsWith("/logo") || rawLogoUrl.startsWith("/favicon")
      ? rawLogoUrl
      : resolveAssetUrl(rawLogoUrl);
  const navLinks = navItems.map((item) => normalizeLink(item, item.label));
  const guestLinks = header.loginLinks.filter((item) =>
    ["/customer/login", "/customer/signup"].includes(String(item.href || "").toLowerCase())
  );
  const hostLinks = header.loginLinks.filter((item) =>
    ["/host/login", "/host/signup"].includes(String(item.href || "").toLowerCase())
  );

  useEffect(() => {
    const handlePointerDown = (event) => {
      if (!loginMenuRef.current?.contains(event.target)) {
        setLoginOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, []);

  useEffect(() => {
    setLoginOpen(false);
    setMobileOpen(false);
  }, [location.pathname, location.search, location.hash]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setMobileOpen(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleMenuItemClick = (href) => {
    setLoginOpen(false);
    setMobileOpen(false);
    if (String(href || "").startsWith("/")) {
      navigate(href);
      return;
    }
    window.location.href = href;
  };

  const handleLogout = async () => {
    await customerAuth.logout();
    setLoginOpen(false);
    setMobileOpen(false);
    navigate("/");
  };

  return (
    <header className="sticky top-0 z-50 border-b border-[rgba(231,223,212,0.8)] bg-[rgba(250,247,242,0.92)] font-jakarta backdrop-blur-xl">
      <div className="landing-container py-3">
        <div className="flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-3">
            {logoUrl ? (
              <img
                src={logoUrl}
                alt={header.brand}
                className="h-16 w-auto object-contain sm:h-20 lg:h-[5.5rem]"
              />
            ) : (
              <>
                <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white shadow-soft-sm ring-1 ring-[var(--border)]">
                  <span className="text-sm font-semibold text-[var(--ink)]">HV</span>
                </span>
                <span className="leading-tight">
                  <span className="block text-[1.45rem] font-homevilas leading-none text-[var(--ink)]">
                    {header.brand}
                  </span>
                  <span className="block pt-1 text-[9px] font-medium uppercase tracking-[0.5em] text-[var(--gold)]">
                    {header.badge}
                  </span>
                </span>
              </>
            )}
          </Link>

          <nav className="hidden lg:flex flex-1 items-center justify-center gap-5">
            {navLinks.map((item) => {
              const content = (
                <div className="flex flex-col items-center gap-2 text-center">
                  <ServiceIcon tone={item.tone}>{item.icon}</ServiceIcon>
                  <span className="text-[11px] font-medium text-[var(--text-muted)]">
                    {item.label}
                  </span>
                </div>
              );
              const href = String(item.href || "#");
              const Wrapper = href.startsWith("/") ? Link : "a";
              const wrapperProps = href.startsWith("/")
                ? { to: href }
                : { href };
              return (
                <Wrapper
                  key={`${item.label}-${item.href}`}
                  {...wrapperProps}
                  className="min-w-[92px] transition hover:-translate-y-0.5 hover:text-[var(--ink)]"
                >
                  {content}
                </Wrapper>
              );
            })}
          </nav>

          <div className="flex items-center gap-3">
            {renderNavLink(
              normalizeLink(
                { label: header.ctaLabel, href: header.ctaLink },
                "Discover stays"
              ),
              "hidden sm:inline-flex items-center rounded-full bg-[var(--cta-dark)] px-5 py-2.5 text-[10px] font-medium uppercase tracking-[0.32em] text-white shadow-soft-sm transition hover:bg-[var(--cta-dark-hover)]"
            )}

            <div ref={loginMenuRef} className="relative hidden md:block">
              <button
                type="button"
                onClick={() => setLoginOpen((prev) => !prev)}
                className="inline-flex items-center gap-2 rounded-full border border-[rgba(15,92,90,0.88)] bg-[linear-gradient(135deg,#10233D_0%,#0F5C5A_58%,#C9A15B_100%)] px-4 py-2.5 text-[10px] font-semibold uppercase tracking-[0.28em] text-white shadow-soft-md transition duration-200 hover:-translate-y-0.5 hover:shadow-[0_18px_36px_rgba(15,92,90,0.24)] focus:outline-none focus:ring-4 focus:ring-[rgba(15,92,90,0.12)]"
              >
                <span>{accountLabel}</span>
                <svg
                  viewBox="0 0 20 20"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.7"
                  className={`h-3.5 w-3.5 transition ${loginOpen ? "rotate-180" : ""}`}
                  aria-hidden="true"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="m5 7.5 5 5 5-5" />
                </svg>
              </button>
              {loginOpen ? (
                <div className="absolute right-0 z-20 mt-2 w-[22rem] rounded-[1.6rem] border border-[var(--border)] bg-[var(--surface)] p-3 shadow-soft-md">
                  <div className="rounded-[1.2rem] border border-[rgba(15,92,90,0.08)] bg-[rgba(15,92,90,0.04)] p-3">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.32em] text-[var(--muted)]">
                      Guest access
                    </p>
                    <div className="mt-2 grid grid-cols-2 gap-2">
                      {customer ? (
                        <>
                          <button type="button" onClick={() => handleMenuItemClick("/guest/dashboard")} className="rounded-xl border border-[var(--border)] bg-white px-3 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.18em] text-[var(--ink)] transition hover:border-[rgba(15,92,90,0.18)] hover:bg-[var(--surface-strong)]">
                            Dashboard
                          </button>
                          <button type="button" onClick={handleLogout} className="rounded-xl border border-[var(--border)] bg-white px-3 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.18em] text-[var(--ink)] transition hover:border-[rgba(15,92,90,0.18)] hover:bg-[var(--surface-strong)]">
                            Logout
                          </button>
                        </>
                      ) : (
                        guestLinks.map((item) => (
                          <button key={`${item.label}-${item.href}`} type="button" onClick={() => handleMenuItemClick(item.href)} className="rounded-xl border border-[var(--border)] bg-white px-3 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.18em] text-[var(--ink)] transition hover:border-[rgba(15,92,90,0.18)] hover:bg-[var(--surface-strong)]">
                            {item.label}
                          </button>
                        ))
                      )}
                    </div>
                  </div>
                  {!customer ? (
                    <div className="mt-3 rounded-[1.2rem] border border-[rgba(201,161,91,0.16)] bg-[rgba(201,161,91,0.08)] p-3">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="text-[10px] font-semibold uppercase tracking-[0.32em] text-[var(--muted)]">
                            Host access
                          </p>
                          <p className="mt-1 text-xs text-[var(--text-muted)]">
                            For partners listing and managing properties.
                          </p>
                        </div>
                      </div>
                      <div className="mt-2 grid grid-cols-2 gap-2">
                        {hostLinks.map((item) => (
                          <button
                            key={`${item.label}-${item.href}`}
                            type="button"
                            onClick={() => handleMenuItemClick(item.href)}
                            className="rounded-xl border border-[var(--border)] bg-white px-3 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.18em] text-[var(--ink)] transition hover:border-[rgba(201,161,91,0.26)] hover:bg-[var(--surface-strong)]"
                          >
                            {item.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>

            <button
              type="button"
              onClick={() => setMobileOpen((prev) => !prev)}
              className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-[var(--border)] bg-white text-[var(--ink)] lg:hidden"
              aria-label="Toggle menu"
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.7"
                className="h-4 w-4"
              >
                <path d="M4 7h16" />
                <path d="M4 12h16" />
                <path d="M4 17h16" />
              </svg>
            </button>
          </div>
        </div>

        {mobileOpen ? (
          <div className="mt-3 max-h-[calc(100vh-7rem)] overflow-y-auto rounded-2xl border border-[var(--border)] bg-[var(--surface)] px-4 py-3 shadow-soft-sm lg:hidden">
            <div className="flex flex-col gap-2">
              {navLinks.map((item) => (
                <div key={`mobile-nav-${item.label}-${item.href}`}>
                  {renderNavLink(
                    item,
                    "block rounded-lg px-2.5 py-2 text-[11px] font-medium uppercase tracking-[0.26em] text-[var(--text-muted)] hover:bg-[var(--surface-strong)] hover:text-[var(--ink)]",
                    () => setMobileOpen(false)
                  )}
                </div>
              ))}
            </div>
            <div className="mt-3 border-t border-[var(--border)] pt-3">
              <p className="px-2.5 text-[10px] uppercase tracking-[0.32em] text-[var(--muted)]">
                Guest access
              </p>
              <div className="mt-2 grid gap-2">
                {customer ? (
                  <>
                    <button type="button" onClick={() => handleMenuItemClick("/guest/dashboard")} className="block w-full rounded-xl border border-[var(--border)] bg-white px-3 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.22em] text-[var(--ink)] hover:bg-[var(--surface-strong)]">
                      Dashboard
                    </button>
                    <button type="button" onClick={handleLogout} className="block w-full rounded-xl border border-[var(--border)] bg-white px-3 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.22em] text-[var(--ink)] hover:bg-[var(--surface-strong)]">
                      Logout
                    </button>
                  </>
                ) : (
                  guestLinks.map((item) => (
                    <button
                      key={`mobile-login-${item.label}-${item.href}`}
                      type="button"
                      onClick={() => handleMenuItemClick(item.href)}
                      className="block w-full rounded-xl border border-[var(--border)] bg-white px-3 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.22em] text-[var(--ink)] hover:bg-[var(--surface-strong)]"
                    >
                      {item.label}
                    </button>
                  ))
                )}
              </div>
            </div>
            {!customer ? <div className="mt-3 border-t border-[var(--border)] pt-3">
              <p className="px-2.5 text-[10px] uppercase tracking-[0.32em] text-[var(--muted)]">
                Host access
              </p>
              <p className="px-2.5 pt-1 text-xs text-[var(--text-muted)]">
                For partners listing and managing properties.
              </p>
              <div className="mt-2 grid gap-2">
                {hostLinks.map((item) => (
                  <button
                    key={`mobile-host-${item.label}-${item.href}`}
                    type="button"
                    onClick={() => handleMenuItemClick(item.href)}
                    className="block w-full rounded-xl border border-[var(--border)] bg-white px-3 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.22em] text-[var(--ink)] hover:bg-[var(--surface-strong)]"
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </div> : null}
          </div>
        ) : null}
      </div>
    </header>
  );
}
